import { useCallback } from 'react'
import type { IconType } from '@/lib/icons'
import {
  Banknote,
  CircleX,
  Clock,
  Film,
  Inbox,
  Link2,
  PartyPopper,
  RotateCw,
  TrendingUp,
  TriangleAlert,
  WifiOff,
} from '@/lib/icons'
import type { DashboardResponse } from '@ayub/shared'
import { formatCompactSom, formatNumber } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ToastViewport, useToast } from '@/components/ui/toast'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

interface DashboardProps {
  data: DashboardResponse | undefined
  isLoading: boolean
  isFetching: boolean
  /** Oxirgi so'rovdan qolgan xato — `data` mavjud bo'lsa ham (eskirgan holatda) kelishi mumkin. */
  error: Error | null
  onRefresh: () => void
}

/**
 * Dashboard — admin panelining bosh sahifasi (`docs/03-app-flow.md`, "Sahifa 1 — Dashboard").
 *
 * Har bir kartochka Phase 6-10'da quriladigan filtrlangan ro'yxatga o'tishi kerak; hozircha
 * ular faqat "tez orada" degan xabar ko'rsatadi. Kelajakda haqiqiy navigatsiya ulash uchun
 * faqat `handleTileClick` ichidagi bitta joyni o'zgartirish kifoya — har bir tugma allaqachon
 * o'zining maqsad manzilini (`navTarget`) yuboradi.
 */
export function Dashboard({ data, isLoading, isFetching, error, onRefresh }: DashboardProps) {
  const { toast, showToast } = useToast()

  const handleTileClick = useCallback(
    (_navTarget: string) => {
      showToast("Tez orada qo'shiladi")
    },
    [showToast],
  )

  if (isLoading) {
    return <DashboardSkeleton />
  }

  if (!data) {
    return <DashboardRetryState error={error} onRetry={onRefresh} />
  }

  const { queue, payouts, month, budget } = data
  const isCalmDay = queue.dueToday === 0 && queue.overdue === 0
  const budgetOverspent = budget.remaining !== null && budget.remaining < 0

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4">
      {error && <NetworkErrorBanner error={error} isFetching={isFetching} onRetry={onRefresh} />}

      <header className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Dashboard</h1>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isFetching}
          aria-label="Yangilash"
          title="Yangilash"
          className="flex size-11 items-center justify-center rounded-button border border-app-border text-app-hint transition-colors duration-150 ease-out hover:bg-app-card disabled:opacity-60"
        >
          <RotateCw size={18} className={cn(isFetching && 'animate-spin')} aria-hidden="true" />
        </button>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {isCalmDay ? (
          <CelebrationTile className="col-span-2 sm:col-span-3" />
        ) : (
          <>
            <DashboardTile
              icon={Clock}
              label="Bugun tekshirish"
              value={formatNumber(queue.dueToday)}
              tone={queue.dueToday > 0 ? 'today' : 'neutral'}
              caption={queue.dueToday > 0 ? 'Bugun tekshiriladi' : undefined}
              onClick={() => handleTileClick('videos:due-today')}
            />
            <DashboardTile
              icon={TriangleAlert}
              label="Kechikkan"
              value={formatNumber(queue.overdue)}
              tone={queue.overdue > 0 ? 'overdue' : 'neutral'}
              caption={queue.overdue > 0 ? "Muddat o'tgan" : undefined}
              onClick={() => handleTileClick('videos:overdue')}
            />
          </>
        )}

        <DashboardTile
          icon={Banknote}
          label="To'lovga tayyor"
          value={formatCompactSom(payouts.readyAmount)}
          tone="brand"
          caption={
            payouts.readyBloggers > 0 ? `${formatNumber(payouts.readyBloggers)} bloger` : undefined
          }
          onClick={() => handleTileClick('payouts:ready')}
        />
        <DashboardTile
          icon={Inbox}
          label="Yangi ariza"
          value={formatNumber(queue.newApplications)}
          tone="neutral"
          onClick={() => handleTileClick('bloggers:new')}
        />
        <DashboardTile
          icon={Film}
          label="Moderatsiyada"
          value={formatNumber(queue.moderation)}
          tone="neutral"
          onClick={() => handleTileClick('videos:moderation')}
        />
        <DashboardTile
          icon={Link2}
          label="Havola kutilmoqda"
          value={formatNumber(queue.awaitingLink)}
          tone="neutral"
          onClick={() => handleTileClick('videos:awaiting-link')}
        />
      </div>

      <button
        type="button"
        onClick={() => handleTileClick('videos:unverified-links')}
        className="flex min-h-11 w-full items-center justify-between gap-3 rounded-card border border-app-border bg-app-card px-4 py-3 text-left transition-colors duration-150 ease-out hover:bg-brand-subtle"
      >
        <span className="inline-flex items-center gap-2 text-sm font-medium">
          <Link2 size={16} className="text-app-hint" aria-hidden="true" />
          Tekshirilmagan havolalar:{' '}
          <span className="tabular font-semibold">{formatNumber(queue.unverifiedLinks)}</span>
        </span>
        {queue.unverifiedLinksOverdue > 0 && (
          <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-urgency-overdue-text">
            <TriangleAlert size={14} aria-hidden="true" />
            {formatNumber(queue.unverifiedLinksOverdue)} ta 48s+
          </span>
        )}
      </button>

      <section className="flex flex-col gap-3 rounded-card border border-app-border bg-app-card p-4">
        <h2 className="inline-flex items-center gap-2 text-base font-semibold">
          <TrendingUp size={18} className="text-brand" aria-hidden="true" />
          Bu oy
        </h2>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <MonthStat label="Faol blogerlar" value={formatNumber(month.activeBloggers)} />
          <MonthStat label="Yangi videolar" value={formatNumber(month.newVideos)} />
          <MonthStat label="Jami prosmotr" value={formatNumber(month.totalViews)} />
          <MonthStat
            label="To'lanadigan"
            value={formatCompactSom(month.committedAmount)}
            hint="Oy davomida o'sib boradi"
          />
        </dl>
      </section>

      <section className="flex flex-col gap-3 rounded-card border border-app-border bg-app-card p-4">
        <h2 className="inline-flex items-center gap-2 text-base font-semibold">
          <Banknote size={18} className="text-brand" aria-hidden="true" />
          Oylik to&apos;lov byudjeti
        </h2>
        {budget.amount === null ? (
          <p className="text-sm text-app-hint">
            Bu oyga byudjet o&apos;rnatilmagan (cheklov yo&apos;q). To&apos;lovlar sahifasidan
            o&apos;rnatishingiz mumkin.
          </p>
        ) : (
          <dl className="grid grid-cols-3 gap-4">
            <MonthStat label="Byudjet" value={formatCompactSom(budget.amount)} />
            <MonthStat label="To'langan" value={formatCompactSom(budget.paidThisMonth)} />
            <div className="flex flex-col gap-1">
              <dt className="text-xs text-app-hint">Qolgan</dt>
              <dd
                className={cn(
                  'tabular text-lg font-semibold',
                  budgetOverspent ? 'text-stage-rejected-text' : 'text-urgency-done-text',
                )}
              >
                {formatCompactSom(budget.remaining ?? 0)}
              </dd>
            </div>
          </dl>
        )}
      </section>

      <ToastViewport message={toast?.message ?? null} />
    </div>
  )
}

type TileTone = 'overdue' | 'today' | 'neutral' | 'brand'

const toneValueClass: Record<TileTone, string> = {
  overdue: 'text-urgency-overdue-text',
  today: 'text-urgency-today-text',
  neutral: 'text-app-text',
  brand: 'text-brand',
}

const toneBorderClass: Record<TileTone, string> = {
  overdue: 'border-l-urgency-overdue',
  today: 'border-l-urgency-today',
  neutral: 'border-l-transparent',
  brand: 'border-l-brand',
}

interface DashboardTileProps {
  icon: IconType
  label: string
  value: string
  tone: TileTone
  caption?: string
  onClick: () => void
}

/** Har bir hisoblagich kartochkasi — bosilganda tegishli filtrlangan ro'yxatga o'tishi kerak. */
function DashboardTile({ icon: Icon, label, value, tone, caption, onClick }: DashboardTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex min-h-[104px] flex-col items-start gap-2 rounded-card border border-l-[3px] border-app-border bg-app-card p-4 text-left shadow-[0_1px_3px_rgba(0,0,0,.06)] transition-all duration-150 ease-out hover:-translate-y-0.5 hover:shadow-[0_4px_10px_rgba(0,0,0,.08)] active:translate-y-0 dark:shadow-none',
        toneBorderClass[tone],
      )}
    >
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-app-hint">
        <Icon size={14} aria-hidden="true" />
        {label}
      </span>
      <span className={cn('tabular text-2xl font-semibold', toneValueClass[tone])}>{value}</span>
      {caption && (
        <span className={cn('text-xs font-medium', toneValueClass[tone])}>{caption}</span>
      )}
    </button>
  )
}

/**
 * "Bugun tekshirish" va "Kechikkan" ikkalasi ham 0 bo'lganda ko'rsatiladi — nol qatoridan
 * ko'ra quvonchli xabar (`docs/03-app-flow.md`, "Bo'sh holatlar"). Tugma emas: bu endi
 * bosiladigan hisoblagich emas, informatsion bo'sh holat xabari.
 */
function CelebrationTile({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex min-h-[104px] flex-col items-center justify-center gap-1 rounded-card border border-app-border bg-app-card p-4 text-center',
        className,
      )}
    >
      <PartyPopper className="text-urgency-done-text" size={22} aria-hidden="true" />
      <p className="text-sm font-semibold text-urgency-done-text">
        Bugun tekshiradigan video yo'q!
      </p>
    </div>
  )
}

function MonthStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-app-hint">{label}</dt>
      <dd className="tabular text-lg font-semibold">{value}</dd>
      {hint && (
        <span className="inline-flex items-center gap-1 text-xs text-urgency-today-text">
          <TriangleAlert size={12} aria-hidden="true" />
          {hint}
        </span>
      )}
    </div>
  )
}

function NetworkErrorBanner({
  error,
  isFetching,
  onRetry,
}: {
  error: Error
  isFetching: boolean
  onRetry: () => void
}) {
  const isServerError = error instanceof ApiError
  return (
    <div className="flex items-center justify-between gap-3 rounded-card border border-urgency-overdue/30 bg-urgency-overdue/10 px-3 py-2 text-sm text-urgency-overdue-text">
      <span className="inline-flex items-center gap-2">
        <WifiOff size={16} aria-hidden="true" />
        {isServerError ? "Server bilan aloqa yo'q." : "Aloqa yo'q."}{' '}
        {isFetching ? 'Qayta urinilmoqda...' : "Ma'lumotlar eskirgan bo'lishi mumkin."}
      </span>
      <button
        type="button"
        onClick={onRetry}
        className="shrink-0 font-medium underline underline-offset-2"
      >
        Qayta urinish
      </button>
    </div>
  )
}

/** Birinchi yuklanish ham muvaffaqiyatsiz tugagan holat — ko'rsatadigan ma'lumot umuman yo'q. */
function DashboardRetryState({ error, onRetry }: { error: Error | null; onRetry: () => void }) {
  const isServerError = error instanceof ApiError
  const Icon = isServerError ? CircleX : WifiOff
  const title = isServerError ? 'Server xatosi' : "Aloqa yo'q"
  const description = isServerError
    ? (error?.message ?? 'Kutilmagan xatolik yuz berdi.')
    : "Internet aloqasini tekshiring va qayta urinib ko'ring."

  return (
    <div className="mx-auto flex min-h-[60dvh] max-w-sm flex-col items-center justify-center gap-3 text-center">
      <Icon size={28} className="text-app-hint" aria-hidden="true" />
      <h1 className="text-base font-semibold">{title}</h1>
      <p className="text-sm text-app-hint">{description}</p>
      <Button className="mt-1" onClick={onRetry}>
        Qayta urinish
      </Button>
    </div>
  )
}

/** Yuklanish paytidagi skeleton — yakuniy ko'rinish bilan bir xil o'lchamda, sakrash bo'lmasin. */
function DashboardSkeleton() {
  return (
    // `aria-busy` — skrinrider uchun: skelet paytida sahifa jim turmasin, "yuklanmoqda"
    // holati e'lon qilinsin, aks holda kontent birdan paydo bo'lganda foydalanuvchi
    // hech qanday signal olmaydi.
    <div className="mx-auto flex max-w-2xl flex-col gap-4" aria-busy="true">
      <header className="flex items-center justify-between gap-3">
        <Skeleton className="h-7 w-32" />
        <Skeleton className="size-11 rounded-button" />
      </header>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={`tile-${index}`} className="min-h-[104px]" />
        ))}
      </div>
      <Skeleton className="h-14" />
      <Skeleton className="h-40" />
    </div>
  )
}
